import React, { useCallback, useEffect, useRef, useState } from "react";
import { Zap } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRole } from "@/context/RoleContext";

const formatEnergy = (value) =>
  new Intl.NumberFormat("es-CO", { maximumFractionDigits: 2 }).format(Number(value || 0));

export default function EnergyBalanceIndicator() {
  const { user, accessMap } = useAuth();
  const { role } = useRole();
  const roles = accessMap?.roles || user?.roles || [];
  const isDirector = role === "superadmin" || roles.includes("director");
  const [balance, setBalance] = useState(null);
  const [delta, setDelta] = useState(null);
  const [pulse, setPulse] = useState(false);
  const balanceRef = useRef(null);
  const requestRef = useRef(false);
  const deltaTimerRef = useRef(null);

  const loadBalance = useCallback(async () => {
    if (isDirector || requestRef.current) return;
    requestRef.current = true;
    try {
      const { data } = await api.get("/api/energy/me/balance", {
        headers: { "Cache-Control": "no-cache" },
      });
      const next = Number(data?.balance || 0);
      const previous = balanceRef.current;
      balanceRef.current = next;
      setBalance(next);
      if (previous !== null && next !== previous) {
        setDelta(next - previous);
        setPulse(true);
        window.clearTimeout(deltaTimerRef.current);
        deltaTimerRef.current = window.setTimeout(() => {
          setDelta(null);
          setPulse(false);
        }, 1800);
      }
    } catch (_) {
      // Never keep a previous account's balance visible after auth/network failure.
      balanceRef.current = null;
      setBalance(null);
      setDelta(null);
      setPulse(false);
    } finally {
      requestRef.current = false;
    }
  }, [isDirector]);

  useEffect(() => {
    balanceRef.current = null;
    setBalance(null);
    setDelta(null);
    setPulse(false);
  }, [user?.id]);

  useEffect(() => {
    if (isDirector || !user?.id) return undefined;
    loadBalance();
    const intervalId = window.setInterval(loadBalance, 4000);
    const sync = () => loadBalance();
    const onVisibility = () => {
      if (document.visibilityState === "visible") loadBalance();
    };
    window.addEventListener("kd9-energy-maybe-changed", sync);
    window.addEventListener("focus", sync);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(deltaTimerRef.current);
      window.removeEventListener("kd9-energy-maybe-changed", sync);
      window.removeEventListener("focus", sync);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isDirector, loadBalance, user?.id]);

  if (isDirector) return null;

  return (
    <Link
      to="/app/energia-kd"
      className={`kd9-energy-balance relative flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-2 sm:px-3.5 transition-all duration-300 ${pulse ? "kd9-energy-balance--updated" : ""}`}
      aria-label={`Wallet Energía KD9. Saldo disponible: ${formatEnergy(balance)}`}
      title="Abrir Wallet de Energía KD9"
      data-testid="topbar-energy-balance"
    >
      <span className="kd9-energy-bolt flex h-7 w-7 items-center justify-center rounded-full" aria-hidden="true">
        <Zap size={17} strokeWidth={2.6} fill="currentColor" />
      </span>
      <span className="font-mont text-sm font-bold tabular-nums tracking-tight">
        {balance === null ? "…" : formatEnergy(balance)}
      </span>
      {delta !== null && (
        <span
          className={`kd9-energy-delta ${delta < 0 ? "kd9-energy-delta--debit" : "kd9-energy-delta--credit"}`}
          aria-live="polite"
        >
          {delta > 0 ? "+" : "−"}{formatEnergy(Math.abs(delta))}
        </span>
      )}
    </Link>
  );
}
